// A room's reflected ceiling plan (docs/claude/rcp.md): the plan of what stands
// above a level mirror `h` mm over the room's floor, seen in it from above — so
// in the plan's own orientation. The mirror's plane CUTS what passes through it
// (the walls, a partition, a heater wall: filled and outlined heavy); what stands
// wholly above it is SEEN (outlined light): a doorway's or an opening's header, a
// pane's head, a bench whose seat is higher, the ceiling's own lines. Or, a view
// of `kind: 'above'`, the plan of what lies on the room's top, seen from above
// (`aboveCeilingDrawing`). Pure and DOM-free (unit-tested); every point in the
// room's LOCAL frame, as the plan's.

import type {
  CeilingPlanView,
  Connection,
  Drawing,
  InnerWall,
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  heaterWallBands,
  innerWallDoorSpans,
  innerWallGlassSpans,
  innerWallSpanBand,
  isPolyRoom,
  outerDoorSpans,
  polyGlassPolys,
  polyInnerWallBand,
  polyInnerWallGlassPoly,
  polyOpeningBand,
  polyWall,
  resolvedOutline,
} from './polyRoom';
import {
  ceilingThicknessOf,
  doorTop,
  glassPaneBase,
  glassPaneTop,
  heaterWallBase,
  heaterWallTop,
  innerWallBase,
  interiorHeight,
  openingArchitrave,
  openingSill,
  partHeight,
  partSpan,
  roomBase,
} from './heights';
import {
  ceilingPlanMarks,
  hasCeiling,
  insidePoint,
  isFlatCeiling,
  resolveCeiling,
  roomBenchSeats,
} from './ceiling';
import {
  GLASS_FILL,
  GLASS_STROKE,
} from './glass';
import {
  type DxfEntity,
  rgbToAci,
} from '../shared/dxf';
import {
  arcPoints,
  instanceToWorld,
  pointInPolygon,
  toInstanceLocal,
} from './geometry';
import { connGeom } from './connections';
import { ON_TOP_TOL } from './mounting';
import {
  backrestColor,
  backrestPlan,
  backrestsOf,
  backrestSpan,
} from './backrest';
import { wallThicknessOf } from './dent';
import {
  bodyOf,
  isTilted,
  planOutline,
} from './tilt';
import {
  ceilingTop,
  coveFigures,
  coveGeometry,
  coveOf,
  FIBRE_MARK,
  LIGHT_ACI,
  LIGHT_AMBER,
  lightLines,
  lightsOf,
  lightTagAnchors,
  partFootprintIn,
  shiftedPath,
  STRIP_APART_PX,
  type TagAnchor,
  tagLeader,
  tagTextAt,
  wallInnerFace,
} from './lights';
import {
  lightTagBox,
  paintLightTag,
} from './tagPaint';
import {
  paintViewTitle,
  type TitleBox,
} from './viewTitle';
import { roomDoor } from './roomDoor';

/** The mirror's height (mm above the room's floor) of a new view. */
export const RCP_HEIGHT = 1200;
/** The lowest a mirror may stand, and how far under the lowest wall top it stays. */
export const RCP_MIN_HEIGHT = 1;

/** What a piece is part of: its colour and its line weight follow it. */
export type RcpRole =
  | 'backrest'
  | 'bench'
  | 'ceiling'
  | 'conn'
  | 'glass'
  | 'header'
  | 'heater'
  | 'light'
  | 'part'
  | 'partition'
  | 'wall';

/** One thing a view draws. `fill`: a filled ring with no outline of its own
 *  (a wall's bands abut, so their seams are never stroked); `line`: a stroked
 *  polyline, `closed` or not. `cut`: the mirror's plane passes through it (heavy),
 *  else it stands above it and is seen (light). `id`: the partition or heater
 *  wall it belongs to, whose colour it takes. */
export interface RcpPiece {
  role: RcpRole;
  kind: 'fill' | 'line';
  cut: boolean;
  pts: Vec2[];
  closed: boolean;
  id?: string;
  /** Out of sight: the walls' inner faces under the ceiling, in the plan above it. */
  dashed?: boolean;
  /** A backrest's up or down strip: painted apart from the other (`LightLine.apart`). */
  apart?: { by: 1 | -1; side: 1 | -1 };
  /** Its own colour: a part's, a connection's (the plan above the ceiling). */
  color?: string;
  /** A part's footprint (the plan above the ceiling): painted with a light fill. */
  part?: boolean;
}

/** A starry sky's fibre end, as the view marks it: a small ring (radius, mm). */
export const RCP_FIBRE_MARK = FIBRE_MARK;

/** A view's drawing: its pieces in paint order (what is seen, then what is
 *  cut), the ceiling's height figure, and the room's outer outline (its extent). */
export interface RcpDrawing {
  pieces: RcpPiece[];
  label: { at: Vec2; value: number } | null;
  /** A cove's second layer: its own underside's height, inside the ceiling's. */
  layerLabel?: { at: Vec2; value: number };
  /** Where the tags of the lights it sees go (a strip's only over the mirror). */
  tags?: TagAnchor[];
  /** Where each part it shows is named: over the mirror, or over the ceiling. */
  names?: Array<{ id: string; at: Vec2 }>;
  outline: Vec2[];
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** A view's stored mirror height, or the default. */
export function rcpHeightOf(view: Pick<CeilingPlanView, 'height'>): number {
  return finite(view.height) ? view.height : RCP_HEIGHT;
}

/** The highest a mirror may stand in a room: under its lowest wall top (a flat
 *  ceiling's underside; a shaped one's lowest along the inner faces), so its
 *  plane cuts every wall. */
export function rcpMaxHeight(room: ProductInstance): number {
  const top = !hasCeiling(room) || isFlatCeiling(room) ? interiorHeight(room) : resolveCeiling(room).interiorEave;
  return Math.max(RCP_MIN_HEIGHT, top - 1);
}

/** The height a view is drawn at: its own, kept inside the room's range (a room
 *  lowered under a stored height draws at its highest). */
export function rcpDrawnHeight(view: Pick<CeilingPlanView, 'height'>, room: ProductInstance): number {
  return Math.min(Math.max(rcpHeightOf(view), RCP_MIN_HEIGHT), rcpMaxHeight(room));
}

/** Why a typed mirror height was kept in range (an English key; `{min}` /
 *  `{max}` in mm), every view's ONE message. */
export const RCP_HEIGHT_KEPT = 'The mirror is kept between {min} and {max} mm above the floor.';

/** Type a view's mirror height: kept between the floor and the room's lowest wall
 *  top. Writes nothing when it would draw the same; null, or the message. */
export function setRcpHeight(view: CeilingPlanView, room: ProductInstance | null, mm: number): { text: string; min: number; max: number } | null {
  if (!Number.isFinite(mm)) return null;
  const max = room ? rcpMaxHeight(room) : Infinity;
  const v = Math.min(Math.max(Math.round(mm), RCP_MIN_HEIGHT), max);
  if (v !== rcpHeightOf(view)) view.height = v;
  return v !== Math.round(mm) ? { text: RCP_HEIGHT_KEPT, min: RCP_MIN_HEIGHT, max: Math.round(max) } : null;
}

/** The spans of [0, len] the gaps leave. */
function solidSpans(len: number, gaps: ReadonlyArray<[number, number]>): Array<[number, number]> {
  const g = gaps.map(([a, b]) => [Math.max(0, Math.min(a, b)), Math.min(len, Math.max(a, b))] as [number, number])
    .filter(([a, b]) => b - a > 1e-6).sort((p, q) => p[0] - q[0]);
  const out: Array<[number, number]> = [];
  let at = 0;
  for (const [a, b] of g) {
    if (a - at > 1e-6) out.push([at, a]);
    at = Math.max(at, b);
  }
  if (len - at > 1e-6) out.push([at, len]);
  return out;
}

const spans = (lo: number, hi: number, h: number): boolean => lo <= h && h < hi;

/** A partition's own top, or none: one with no height of its own reaches the
 *  underside, which stands over every mirror a room may hold (`rcpMaxHeight`). */
const partitionTop = (iw: InnerWall): number => (iw.height == null ? Infinity : innerWallBase(iw) + iw.height);

/** A room's reflected ceiling plan at mirror height `h` (mm above its floor),
 *  with the parts of `src` standing in the room over the mirror (a control
 *  unit's sensor under the ceiling …). A fibre kit's projector and arcs lie over
 *  the ceiling, out of its sight: the plan above the ceiling draws them. */
export function rcpDrawing(room: ProductInstance, h: number, src?: PartSource): RcpDrawing {
  const pieces: RcpPiece[] = [];
  if (!isPolyRoom(room)) return { pieces, label: null, outline: [] };
  const fill = (role: RcpRole, pts: Vec2[], id?: string): void => { if (pts.length >= 3) pieces.push({ role, kind: 'fill', cut: true, pts, closed: true, ...(id ? { id } : {}) }); };
  const line = (role: RcpRole, pts: Vec2[], cut: boolean, closed: boolean, id?: string): void => {
    if (pts.length >= 2) pieces.push({ role, kind: 'line', cut, pts, closed, ...(id ? { id } : {}) });
  };
  const T = ceilingThicknessOf(room);

  // Seen, behind everything the plane cuts: the ceiling's own lines first, then
  // a bench whose seat stands over the mirror.
  if (T > 0 && hasCeiling(room) && !isFlatCeiling(room)) {
    for (const l of ceilingPlanMarks(room).lines) line('ceiling', l.pts, false, !!l.closed);
  }
  for (const b of roomBenchSeats(room)) if (b.seat > h) line('bench', b.poly, false, true, b.id);
  // A backrest: seen over the mirror, cut where the mirror passes through its boards.
  for (const br of backrestsOf(room)) {
    const { z0, z1 } = backrestSpan(room, br);
    for (const band of backrestPlan(room, br)) {
      if (spans(z0, z1, h)) { fill('backrest', band, br.id); line('backrest', band, true, true, br.id); } else if (z0 > h) line('backrest', band, false, true, br.id);
    }
  }
  // The lights: a strip on a bench or a backrest seen only where it stands over
  // the mirror, the ceiling ones over every mirror (a sky's fibres, not its zone).
  for (const l of lightLines(room, undefined, (z) => z > h)) {
    if (l.zone) continue;
    pieces.push({ role: l.ceiling ? 'ceiling' : 'light', kind: 'line', cut: false, pts: l.pts, closed: l.closed, ...(l.id ? { id: l.id } : {}), ...(l.apart ? { apart: l.apart } : {}) });
  }
  const cove = coveOf(room), cg = cove ? coveGeometry(room) : null;

  // A doorway's, an opening's or a pane's head over the mirror is seen: its band.
  for (const d of outerDoorSpans(room)) {
    const band = roomDoor(room, d.door)?.gap ?? null;
    if (band && h < doorTop(room, d.door)) line('header', band, false, true);
  }
  for (const o of room.openings || []) {
    if (o.innerWallId == null && spans(openingSill(o), openingArchitrave(room, o), h)) line('header', polyOpeningBand(room, o), false, true);
  }
  for (const g of room.glass || []) {
    if (g.innerWallId != null || !spans(glassPaneBase(g), glassPaneTop(room, g), h)) continue;
    line('header', polyOpeningBand(room, g), false, true);
    for (const q of polyGlassPolys(room, g)) line('glass', q, true, true);
  }

  // Heater walls and partitions: cut where the plane passes through them, seen
  // where they hang wholly above it, nothing where they stop under it.
  for (const { wall, poly } of heaterWallBands(room)) {
    const lo = heaterWallBase(wall), hi = heaterWallTop(wall);
    if (spans(lo, hi, h)) { fill('heater', poly, wall.id); line('heater', poly, true, true, wall.id); } else if (lo > h) line('heater', poly, false, true, wall.id);
  }
  for (const iw of room.innerWalls || []) {
    if (iw.edge == null) continue;
    const lo = innerWallBase(iw), hi = partitionTop(iw);
    if (lo > h) { line('partition', polyInnerWallBand(room, iw), false, true, iw.id); continue; }
    if (!spans(lo, hi, h)) continue;
    const gaps: Array<[number, number]> = [];
    for (const d of innerWallDoorSpans(room, iw.id)) {
      if (h >= doorTop(room, d.door)) continue;
      gaps.push([d.start, d.end]);
      const band = innerWallSpanBand(room, iw, d.start, d.end);
      if (band) line('header', band, false, true);
    }
    for (const g of innerWallGlassSpans(room, iw.id)) {
      if (!spans(glassPaneBase(g.seg), glassPaneTop(room, g.seg), h)) continue;
      gaps.push([g.start, g.end]);
      const band = innerWallSpanBand(room, iw, g.start, g.end);
      if (band) line('header', band, false, true);
      line('glass', polyInnerWallGlassPoly(room, g.seg), true, true);
    }
    for (const [a, b] of solidSpans(Math.max(0, iw.length), gaps)) {
      const q = innerWallSpanBand(room, iw, a, b);
      if (q) { fill('partition', q, iw.id); line('partition', q, true, true, iw.id); }
    }
  }

  // The parts standing in the room over the mirror: cut where its plane passes
  // through them, seen where they stand wholly over it (a sensor under the
  // ceiling), each named; under it, left out.
  const names: NonNullable<RcpDrawing['names']> = [];
  for (const p of src?.drawing.instances ?? []) {
    if ((src?.keepPart && !src.keepPart(p)) || !standsUnderCeiling(room, p)) continue;
    const [lo, hi] = partSpan(p).map((z) => z - roomBase(room));
    if (hi <= h) continue;
    const role: RcpRole = p.category === 'lighting' ? 'light' : 'part', pts = partPlanIn(room, p), cut = lo <= h;
    if (cut) pieces.push({ role, kind: 'fill', cut, pts, closed: true, id: p.id, color: p.color });
    pieces.push({ role, kind: 'line', cut, pts, closed: true, id: p.id, color: p.color, part: true });
    names.push({ id: p.id, at: toInstanceLocal(room, { x: p.x, y: p.y }) });
  }

  // The room's walls, cut: filled band by band, outlined along their faces, their
  // jambs and their rounded corners' arcs only.
  const pw = polyWall(room, { cutAt: h });
  for (const q of pw.fill) fill('wall', q);
  for (const [a, b] of [...pw.outer, ...pw.inner, ...pw.jambs]) line('wall', [a, b], true, false);
  for (const c of pw.cornerArcs) line('wall', arcPoints(c.center, c.from, c.to, c.radius), true, false);

  const tags = lightTagAnchors(room).filter((a) => a.z == null || a.z > h);
  // The ceiling's height: the underside's highest, where it is.
  let label: RcpDrawing['label'] = null;
  if (T > 0 && hasCeiling(room)) {
    if (isFlatCeiling(room)) label = { at: insidePoint(resolvedOutline(room, wallThicknessOf(room))), value: interiorHeight(room) };
    else {
      const cf = resolveCeiling(room);
      label = { at: cf.flat ? insidePoint(cf.innerRing) : ceilingPlanMarks(room).crown?.at ?? cf.crownAt ?? insidePoint(cf.innerRing), value: cf.interiorCrown };
    }
  }
  // A flat ceiling's cove: the second layer's own height in it, the ceiling's in
  // the slot along its first wall.
  const layer = cg?.secondLayer, slot = cove && layer ? wallInnerFace(room, cove.walls[0]) : null;
  if (layer && label && slot && cove) {
    const g = coveFigures(cove).gap / 2;
    label.at = { x: (slot.a.x + slot.b.x) / 2 + slot.n.x * g, y: (slot.a.y + slot.b.y) / 2 + slot.n.y * g };
    return { pieces, label, layerLabel: { at: insidePoint(layer.ring), value: layer.z0 }, outline: resolvedOutline(room, 0), tags, names };
  }
  return { pieces, label, outline: resolvedOutline(room, 0), tags, names };
}

// ---- The plan above the ceiling (`kind: 'above'`) ----------------------------------

/** What a view looks at in the drawing: its parts (both kinds) and its
 *  connections (the plan above the ceiling), each kept only while `keepPart` /
 *  `keepConn` show it (a hidden layer, a category or a discipline filtered out). */
export interface PartSource {
  drawing: Drawing;
  keepPart?: (p: ProductInstance) => boolean;
  keepConn?: (c: Connection) => boolean;
}

/** Where a part over a room's plan lies: over its ceiling (its base at the
 *  ceiling's underside over it or higher: on its top, or in the void over it),
 *  under it, or not over the room (its middle outside its outline; a room). */
function partLevel(room: ProductInstance, part: ProductInstance): 'above' | 'under' | null {
  if (part === room || part.autoBenches || part.id === room.id) return null;
  const at = toInstanceLocal(room, { x: part.x, y: part.y });
  if (!pointInPolygon(at, resolvedOutline(room, 0))) return null;
  const base = partSpan(part)[0] - roomBase(room);
  return base >= ceilingTop(room)(at) - ceilingThicknessOf(room) - ON_TOP_TOL ? 'above' : 'under';
}

/** Whether a part lies over a room's ceiling (`partLevel`). Never a room. */
export function liesAboveCeiling(room: ProductInstance, part: ProductInstance): boolean {
  return partLevel(room, part) === 'above';
}

/** Whether a part stands in a room, under its ceiling (`partLevel`). Never a room. */
export function standsUnderCeiling(room: ProductInstance, part: ProductInstance): boolean {
  return partLevel(room, part) === 'under';
}

/** A part's plan in a room's local frame: its box, or a tilted one's outline
 *  from above (a sensor stood on a wall). */
function partPlanIn(room: ProductInstance, part: ProductInstance): Vec2[] {
  if (!isTilted(part)) return partFootprintIn(room, part);
  return planOutline(bodyOf(part, partSpan(part)[0], { w: part.width, d: part.depth, h: partHeight(part) })).map((p) => toInstanceLocal(room, p));
}

/** The spans of a 3D path (world, mm above the ground) that run over a room's
 *  ceiling, in the room's local frame: inside its outline, at its top over each
 *  point or higher. Each a polyline in plan; a riser leaves none. */
export function pathAboveCeiling(room: ProductInstance, path: ReadonlyArray<{ x: number; y: number; z: number }>): Vec2[][] {
  const ring = resolvedOutline(room, 0), top = ceilingTop(room), base = roomBase(room);
  const out: Vec2[][] = [];
  let run: Vec2[] | null = null;
  const close = (): void => { if (run && run.length >= 2) out.push(run); run = null; };
  for (let k = 0; k + 1 < path.length; k++) {
    const a3 = path[k], b3 = path[k + 1];
    const a = toInstanceLocal(room, a3), b = toInstanceLocal(room, b3);
    if (Math.hypot(b.x - a.x, b.y - a.y) < 1e-6) continue;
    // Where the segment crosses the outline: each piece between is wholly in or out.
    const ts = [0, 1];
    for (let i = 0; i < ring.length; i++) {
      const t = segParam(a, b, ring[i], ring[(i + 1) % ring.length]);
      if (t != null) ts.push(t);
    }
    ts.sort((p, q) => p - q);
    for (let i = 0; i + 1 < ts.length; i++) {
      const t0 = ts[i], t1 = ts[i + 1];
      if (t1 - t0 < 1e-9) continue;
      const tm = (t0 + t1) / 2, m = lerp2(a, b, tm), zm = a3.z + (b3.z - a3.z) * tm - base;
      if (!pointInPolygon(m, ring) || zm < top(m) - ON_TOP_TOL) { close(); continue; }
      const p0 = lerp2(a, b, t0), p1 = lerp2(a, b, t1);
      const last: Vec2 | undefined = run ? run[run.length - 1] : undefined;
      if (!run || !last || Math.hypot(last.x - p0.x, last.y - p0.y) > 1e-6) { close(); run = [p0]; }
      run!.push(p1);
    }
  }
  close();
  return out;
}

const lerp2 = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });

/** Where along a→b (0…1) it crosses the segment c→d, strictly inside a→b. */
function segParam(a: Vec2, b: Vec2, c: Vec2, d: Vec2): number | null {
  const rx = b.x - a.x, ry = b.y - a.y, sx = d.x - c.x, sy = d.y - c.y;
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / den, u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / den;
  return t > 1e-9 && t < 1 - 1e-9 && u >= -1e-9 && u <= 1 + 1e-9 ? t : null;
}

/** A room's plan above its ceiling: its outline from above (the walls' inner
 *  faces under the ceiling dashed), a shaped ceiling's own lines, every light
 *  that goes through it — a spot, a fibre end — and each fibre kit's arcs, then
 *  every part lying over it (`liesAboveCeiling`: a projector, a junction box …,
 *  named) and the connections that run over it. Its figure is the ceiling's top. */
export function aboveCeilingDrawing(room: ProductInstance, src?: PartSource): RcpDrawing {
  const pieces: RcpPiece[] = [];
  if (!isPolyRoom(room)) return { pieces, label: null, outline: [] };
  const line = (pc: Omit<RcpPiece, 'kind' | 'cut'> & { cut?: boolean }): void => {
    if (pc.pts.length >= 2) pieces.push({ kind: 'line', cut: false, ...pc });
  };
  const T = ceilingThicknessOf(room), top = ceilingTop(room);
  const insts = src?.drawing.instances ?? [], parts = new Map(insts.map((i) => [i.id, i]));

  if (T > 0 && hasCeiling(room) && !isFlatCeiling(room)) {
    for (const l of ceilingPlanMarks(room).lines) line({ role: 'ceiling', pts: l.pts, closed: !!l.closed });
  }
  line({ role: 'wall', pts: resolvedOutline(room, wallThicknessOf(room)), closed: true, dashed: true });
  // The lights through the ceiling — a spot's ring and cross, a fibre end's ring —
  // and the kits' arcs over it, seen. A kit's projector is drawn as the part it is.
  const through = new Set(lightsOf(room).filter((l) => l.kind === 'spot' || l.kind === 'sky').map((l) => l.id));
  for (const l of lightLines(room, parts, () => false)) {
    if (l.zone || (l.hidden && l.closed) || !l.id || (!through.has(l.id) && !l.hidden)) continue;
    line({ role: 'light', pts: l.pts, closed: l.closed, id: l.id });
  }
  for (const c of src?.drawing.connections ?? []) {
    if (!src || (src.keepConn && !src.keepConn(c))) continue;
    const g = connGeom(src.drawing, c);
    if (!g) continue;
    for (const pts of pathAboveCeiling(room, g.path.pts)) line({ role: 'conn', pts, closed: false, id: c.id, color: g.color });
  }
  const names: NonNullable<RcpDrawing['names']> = [];
  for (const p of insts) {
    if ((src?.keepPart && !src.keepPart(p)) || !liesAboveCeiling(room, p)) continue;
    line({ role: p.category === 'lighting' ? 'light' : 'part', pts: partPlanIn(room, p), closed: true, id: p.id, color: p.color, part: true });
    names.push({ id: p.id, at: toInstanceLocal(room, { x: p.x, y: p.y }) });
  }
  line({ role: 'wall', pts: resolvedOutline(room, 0), closed: true, cut: true });

  const tags = lightTagAnchors(room).filter((a) => through.has(a.id));
  let label: RcpDrawing['label'] = null;
  if (T > 0 && hasCeiling(room)) {
    const at = isFlatCeiling(room) ? insidePoint(resolvedOutline(room, wallThicknessOf(room))) : (() => {
      const cf = resolveCeiling(room);
      return cf.flat ? insidePoint(cf.innerRing) : ceilingPlanMarks(room).crown?.at ?? cf.crownAt ?? insidePoint(cf.innerRing);
    })();
    label = { at, value: top(at) };
  }
  return { pieces, label, outline: resolvedOutline(room, 0), tags, names };
}

/** A view's drawing, whichever kind it is: the plan above the ceiling or the
 *  reflected ceiling plan at its mirror (`src` gives either the drawing's parts;
 *  without one it has only the room's own lines). */
export function ceilingViewDrawing(view: CeilingPlanView, room: ProductInstance, src?: PartSource): RcpDrawing {
  return view.kind === 'above' ? aboveCeilingDrawing(room, src) : rcpDrawing(room, rcpDrawnHeight(view, room), src);
}

// ---- Where a view stands, and how it is painted ------------------------------------

/** A view's map from its room's local frame to the world: the room's own, moved
 *  by the view's offset — so the view turns and moves with its room. */
export function rcpWorld(view: Pick<CeilingPlanView, 'offset'>, room: Pick<ProductInstance, 'x' | 'y' | 'rotation'>): (p: Vec2) => Vec2 {
  return (p) => { const w = instanceToWorld(room, p); return { x: w.x + view.offset.x, y: w.y + view.offset.y }; };
}

/** A view's outline in the world: its room's outer outline where the view stands. */
export function rcpOutline(view: Pick<CeilingPlanView, 'offset'>, room: ProductInstance): Vec2[] {
  return resolvedOutline(room, 0).map(rcpWorld(view, room));
}

/** Where a view's title hangs (world): centred under its outline, 300 mm clear. */
export function rcpTitleAt(outline: readonly Vec2[]): Vec2 {
  const xs = outline.map((p) => p.x), ys = outline.map((p) => p.y);
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: Math.min(...ys) - 300 };
}

/** Where a view's title hangs, as drawn, printed and exported: `rcpTitleAt` moved
 *  by its `titleOffset` — null once it is deleted. */
export function rcpTitlePos(view: Pick<CeilingPlanView, 'titleOffset' | 'titleHidden'>, outline: readonly Vec2[]): Vec2 | null {
  if (view.titleHidden || outline.length < 3) return null;
  const at = rcpTitleAt(outline), o = view.titleOffset;
  return o ? { x: at.x + o.x, y: at.y + o.y } : at;
}

/** The annotation ink of a view (a section's), and its selected blue. */
export const RCP_INK = '#c0392b';
export const RCP_SELECTED = '#4aa3ff';

function shade(hex: string, k: number, toWhite: boolean): string {
  const h = hex.replace('#', '').slice(0, 6);
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  const m = (v: number): number => Math.round(toWhite ? v + (255 - v) * k : v * k);
  return `rgb(${m((n >> 16) & 255)}, ${m((n >> 8) & 255)}, ${m(n & 255)})`;
}

/** 45° hatch lines over a screen polygon, clipped to it (a section's cut face). */
function hatch(ctx: CanvasRenderingContext2D, poly: readonly Vec2[], color: string): void {
  const xs = poly.map((p) => p.x), ys = poly.map((p) => p.y);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  ctx.save();
  ctx.beginPath();
  poly.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath(); ctx.clip();
  ctx.strokeStyle = color; ctx.globalAlpha = 0.5; ctx.lineWidth = 0.75;
  ctx.beginPath();
  for (let d = x0 - (y1 - y0); d < x1; d += 6) { ctx.moveTo(d, y1); ctx.lineTo(d + (y1 - y0), y0); }
  ctx.stroke();
  ctx.restore();
}

/** A piece's own colour — a heater wall's, a partition's, a backrest's — or
 *  undefined where it takes the room's wall colour. */
function pieceColour(room: ProductInstance, pc: RcpPiece): string | undefined {
  if (pc.role === 'heater') return room.heaterWalls?.find((x) => x.id === pc.id)?.color;
  if (pc.role === 'partition') return room.innerWalls?.find((x) => x.id === pc.id)?.color;
  if (pc.role === 'backrest') { const br = room.backrests?.find((x) => x.id === pc.id); return br ? backrestColor(room, br) : undefined; }
  return pc.color;
}

export interface RcpPaint {
  /** The room's wall colour (a partition without one of its own takes it). */
  wall: string;
  selected: boolean;
  /** The title under the view (translated, its height filled in). */
  title: string;
  /** A length in the drawing's dimension style. */
  figure: (mm: number) => string;
  /** The lights' tags by tag key (L1 …). */
  tags?: ReadonlyMap<string, string>;
  /** false: the lights hidden (the Lighting toggle) — their pieces and tags. */
  lights?: boolean;
  /** Its title is the selection: painted in the selection blue, boxed. */
  titleSelected?: boolean;
  /** Told where its title was painted (screen px): the plan's press finds it there. */
  onTitle?: (box: TitleBox) => void;
  /** The drawing's parts it looks at, and what it shows (`PartSource`). */
  source?: PartSource;
  /** A part's name, as the view writes it (the drawing's language); none, no names
   *  (the plan's Labels toggle off). */
  partName?: (id: string) => string;
}

/** Paint a view: the cut faces as a section paints them (a light ground, hatched,
 *  outlined), what is seen as thin lines, the glass as the plan's, the ceiling's
 *  height figure, the title under it, and — selected — its outline. */
export function drawCeilingPlan(ctx: CanvasRenderingContext2D, vp: { worldToScreen(p: Vec2): Vec2 }, view: CeilingPlanView, room: ProductInstance, o: RcpPaint): void {
  const d = ceilingViewDrawing(view, room, o.source);
  const W = rcpWorld(view, room), S = (p: Vec2): Vec2 => vp.worldToScreen(W(p));
  const colour = (pc: RcpPiece): string => pieceColour(room, pc) ?? o.wall;
  // A backrest's up and down strips, one line in plan, are painted a few px apart.
  const o0 = S({ x: 0, y: 0 }), o1 = S({ x: 1, y: 0 }), apart = STRIP_APART_PX / (Math.hypot(o1.x - o0.x, o1.y - o0.y) || 1);
  ctx.save();
  ctx.setLineDash([]);
  for (const pc of d.pieces) {
    if (pc.role === 'light' && o.lights === false) continue;
    const pts = (pc.apart ? shiftedPath(pc.pts, pc.apart.side, pc.apart.by * apart) : pc.pts).map(S), c = colour(pc);
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    if (pc.closed) ctx.closePath();
    if (pc.kind === 'fill') {
      ctx.fillStyle = shade(c, 0.78, true);
      ctx.fill();
      hatch(ctx, pts, c);
      continue;
    }
    if (pc.role === 'glass') {
      ctx.fillStyle = GLASS_FILL; ctx.fill();
      ctx.strokeStyle = GLASS_STROKE; ctx.lineWidth = 1.5;
    } else if (pc.role === 'light') {
      if (pc.part && !pc.cut) { ctx.fillStyle = 'rgba(232, 160, 32, 0.14)'; ctx.fill(); }
      ctx.strokeStyle = LIGHT_AMBER; ctx.lineWidth = pc.dashed ? 1 : 1.6;
      ctx.setLineDash(pc.dashed ? [6, 4] : []);
    } else if (pc.role === 'conn') {
      ctx.strokeStyle = c; ctx.lineWidth = 2;
    } else {
      if (pc.part && !pc.cut) { ctx.fillStyle = shade(c, 0.8, true); ctx.fill(); }
      ctx.strokeStyle = shade(c, pc.cut || pc.part ? 0.55 : 0.75, false);
      ctx.lineWidth = pc.cut || pc.part ? 1.4 : 1;
      ctx.setLineDash(pc.dashed ? [6, 4] : []);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }
  // Each part's name in its middle.
  for (const n of d.names ?? []) {
    const pc = d.pieces.find((x) => x.part && x.id === n.id), name = o.partName?.(n.id);
    if (!name || (pc?.role === 'light' && o.lights === false)) continue;
    const at = S(n.at);
    ctx.fillStyle = o.selected ? RCP_SELECTED : RCP_INK; ctx.font = '11px system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(name, at.x, at.y);
  }
  // The lights' tags, each beside its light — boxed, in the light's amber —, or
  // where it was moved to, with its leader.
  for (const a of o.lights === false ? [] : d.tags ?? []) {
    const tag = o.tags?.get(a.key);
    if (!tag) continue;
    const c = S(a.at);
    let at = { x: c.x + 14, y: c.y - 14 };
    if (a.offset) at = S(tagTextAt(a));
    else if (a.side) {
      const s = S({ x: a.at.x + a.side.x, y: a.at.y + a.side.y }), L = Math.hypot(s.x - c.x, s.y - c.y) || 1;
      at = { x: c.x + ((s.x - c.x) / L) * 22, y: c.y + ((s.y - c.y) / L) * 22 };
    }
    paintLightTag(ctx, tag, lightTagBox(ctx, tag, at), a.offset ? c : null);
  }
  const ink = o.selected ? RCP_SELECTED : RCP_INK;
  ctx.fillStyle = ink;
  ctx.textAlign = 'center';
  for (const l of [d.label, d.layerLabel]) {
    if (!l) continue;
    const at = S(l.at);
    ctx.font = '12px system-ui, sans-serif'; ctx.textBaseline = 'middle';
    ctx.fillText(`▲ ${o.figure(l.value)}`, at.x, at.y);
  }
  const outline = d.outline.map(W);
  if (outline.length >= 3) {
    const at = rcpTitlePos(view, outline);
    if (at) o.onTitle?.(paintViewTitle(ctx, vp.worldToScreen(at), o.title, ink, o.titleSelected));
    if (o.selected) {
      ctx.strokeStyle = RCP_SELECTED; ctx.lineWidth = 1.5; ctx.setLineDash([6, 4]);
      ctx.beginPath();
      outline.map((p) => vp.worldToScreen(p)).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath(); ctx.stroke(); ctx.setLineDash([]);
    }
  }
  ctx.restore();
}

/** A view as DXF entities, in the world: the cut faces as SOLIDs in their colour
 *  (every one first: R12 has no draw order, so a SOLID after a line could hide
 *  it), then every line — a cut one in the layer's colour, a seen one grey, the
 *  glass cyan, a light amber (none, nor its tag, with `lights` false) —, then the
 *  ceiling's height figure, the lights' tags and the title under it. */
export function rcpToDxf(view: CeilingPlanView, room: ProductInstance, o: Pick<RcpPaint, 'wall' | 'title' | 'figure' | 'tags' | 'lights' | 'source' | 'partName'>): DxfEntity[] {
  const d = ceilingViewDrawing(view, room, o.source), W = rcpWorld(view, room);
  const colour = (pc: RcpPiece): string => pieceColour(room, pc) ?? o.wall;
  const fills: DxfEntity[] = [], lines: DxfEntity[] = [];
  for (const pc of d.pieces) {
    const points = pc.pts.map(W);
    if (pc.role === 'light' && o.lights === false) continue;
    if (pc.kind === 'fill') { fills.push({ kind: 'hatch', loops: [points], color: rgbToAci(colour(pc)) }); continue; }
    const color = pc.role === 'glass' ? 4 : pc.role === 'light' ? LIGHT_ACI : pc.color ? rgbToAci(pc.color) : pc.cut ? undefined : 8;
    const ltype = pc.dashed ? { ltype: 'DASHED' as const } : {};
    lines.push(points.length === 2 && !pc.closed
      ? { kind: 'line', a: points[0], b: points[1], ...(color ? { color } : {}), ...ltype }
      : { kind: 'polyline', points, closed: pc.closed, ...(color ? { color } : {}), ...ltype });
  }
  const texts: DxfEntity[] = [];
  for (const l of [d.label, d.layerLabel]) if (l) texts.push({ kind: 'text', pos: W(l.at), height: 120, text: o.figure(l.value), align: 'center', baseline: 'middle' });
  for (const n of d.names ?? []) {
    const pc = d.pieces.find((x) => x.part && x.id === n.id), name = o.partName?.(n.id);
    if (name && !(pc?.role === 'light' && o.lights === false)) texts.push({ kind: 'text', pos: W(n.at), height: 80, text: name, align: 'center', baseline: 'middle' });
  }
  for (const a of o.lights === false ? [] : d.tags ?? []) {
    const tag = o.tags?.get(a.key);
    if (!tag) continue;
    texts.push({ kind: 'text', pos: W(tagTextAt(a)), height: 100, text: tag, align: 'center', baseline: 'middle', color: LIGHT_ACI });
    const leader = tagLeader(a);
    if (leader) lines.push({ kind: 'line', a: W(leader[0]), b: W(leader[1]), color: LIGHT_ACI });
  }
  const outline = d.outline.map(W);
  const titleAt = rcpTitlePos(view, outline);
  if (titleAt) texts.push({ kind: 'text', pos: titleAt, height: 200, text: o.title, align: 'center', baseline: 'top' });
  return [...fills, ...lines, ...texts];
}

